import { random01 } from '@/flame/randomize'
import { recordEntries } from '@/utils/record'
import type { FlameDescriptor } from '@/flame/schema/flameSchema'
import type { EasingCurve, TimelineState } from '@/utils/timeline'

/* ──── Module-level randomization ──── */

const EASING_POOL: EasingCurve[] = [
  'easeInOut',
  'easeInOut',
  'easeInOut',
  'linear',
  'linear',
  'easeOut',
  'easeOut',
  'easeIn',
  'bounce',
  'elastic',
]

function randomEasing(): EasingCurve {
  return EASING_POOL[Math.floor(Math.random() * EASING_POOL.length)]!
}

export function randomizeColorsParams(
  flame: FlameDescriptor,
  timeline: TimelineState,
) {
  const endFrame = timeline.config().endFrame
  const transforms = recordEntries(flame.transforms)
  if (transforms.length === 0) return

  const totalSteps = transforms.length * 3 + 6
  const segmentSize = endFrame / totalSteps

  let idx = 0
  for (const [tid] of transforms) {
    const tidStr = String(tid)
    const count = 2 + Math.floor(Math.random() * 2)
    for (let k = 0; k < count; k++) {
      const frame = Math.min(
        endFrame,
        Math.round(idx * segmentSize + Math.random() * segmentSize * 2),
      )
      const easing = randomEasing()
      const cx = random01()
      const cy = random01()
      timeline.addKeyframe(`transform.${tidStr}.color.x`, frame, cx, easing)
      timeline.addKeyframe(`transform.${tidStr}.color.y`, frame, cy, easing)
      idx++
    }
  }

  const paletteParams = ['paletteSpeed', 'palettePhase'] as const
  for (const param of paletteParams) {
    for (let k = 0; k < 3; k++) {
      const frame = Math.min(
        endFrame,
        Math.round(idx * segmentSize + Math.random() * segmentSize * 2),
      )
      timeline.addKeyframe(
        param,
        frame,
        wildValueForPath(param),
        randomEasing(),
      )
      idx++
    }
  }
}

export type PresetDef = {
  label: string
  category: 'Camera' | 'Render' | 'Color' | 'Affine'
  apply: (flame: FlameDescriptor, timeline: TimelineState) => void
}

type RawPresetDef = {
  label: string
  apply: (flame: FlameDescriptor, timeline: TimelineState) => void
}

function wildValueForPath(path: string): number {
  if (path.endsWith('.probability') || path.includes('.color.')) {
    return random01()
  }
  if (path === 'skipIters') {
    return Math.floor(random01() * 30)
  }
  if (path === 'exposure') {
    return (random01() - 0.5) * 16
  }
  if (path === 'vibrancy') {
    return random01() * 3
  }
  if (path === 'contrast') {
    return 0.01 + random01() * 19.99
  }
  if (path === 'gamma') {
    return 0.1 + random01() * 7.9
  }
  if (path === 'highlightPower') {
    return random01() * 2
  }
  if (path === 'paletteSpeed') {
    return random01() * 30
  }
  if (path === 'palettePhase') {
    return random01()
  }
  if (path.startsWith('camera.')) {
    if (path === 'camera.zoom') return 0.1 + random01() * 5
    return (random01() - 0.5) * 2
  }
  // Affine coefficients: 30% chance of zero-centered [-1,1], 70% [0,1]
  if (random01() < 0.3) return (random01() - 0.5) * 2
  return random01()
}

/* ──── Presets ──── */

function frameRange(timeline: TimelineState): {
  start: number
  mid: number
  end: number
} {
  const cfg = timeline.config()
  const end = cfg.endFrame
  return { start: 0, mid: Math.round(end / 2), end }
}

const EASE_IN_OUT: EasingCurve = 'easeInOut'
const EASE_OUT: EasingCurve = 'easeOut'
const LINEAR: EasingCurve = 'linear'

export function buildPresets(is3D: boolean): PresetDef[] {
  const mapPresets = (
    list: RawPresetDef[],
    category: PresetDef['category'],
  ): PresetDef[] => list.map((p) => ({ ...p, category }))

  return [
    ...mapPresets(is3D ? camera3DPresets() : cameraPresets(), 'Camera'),
    ...mapPresets(renderPresets(), 'Render'),
    ...mapPresets(colorPresets(), 'Color'),
    ...mapPresets(affinePresets(), 'Affine'),
  ]
}

/**
 * "Smart" animation: apply one random curated preset from each category
 * (Camera, Render, Color, Affine) for a full, coherent multi-aspect loop.
 * Categories write disjoint parameter paths (camera.* vs exposure/gamma vs
 * palette/color vs affine/finalTransform), so one-per-category composes cleanly
 * without presets fighting over the same keyframes.
 */
export function smartRandomAnimation(
  flame: FlameDescriptor,
  timeline: TimelineState,
) {
  const byCategory = new Map<PresetDef['category'], PresetDef[]>()
  for (const preset of buildPresets(flame.renderSettings.dimensions === 3)) {
    const list = byCategory.get(preset.category)
    if (list) list.push(preset)
    else byCategory.set(preset.category, [preset])
  }
  for (const list of byCategory.values()) {
    const pick = list[Math.floor(Math.random() * list.length)]
    pick?.apply(flame, timeline)
  }
}

function camera3DPresets(): RawPresetDef[] {
  return [
    {
      label: '3D Orbit',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera3D
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera3D.theta', start, cam.theta, LINEAR)
        timeline.addKeyframe(
          'camera3D.theta',
          end,
          cam.theta + Math.PI * 2,
          LINEAR,
        )
      },
    },
    {
      label: '3D Orbit & Sine',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera3D
        if (!cam) return
        const { start, end } = frameRange(timeline)
        const duration = end - start

        timeline.addKeyframe('camera3D.theta', start, cam.theta, LINEAR)
        timeline.addKeyframe(
          'camera3D.theta',
          end,
          cam.theta + Math.PI * 2,
          LINEAR,
        )

        const basePhi = cam.phi > Math.PI / 3 ? Math.PI / 4 : cam.phi
        const cycles = 1
        const amp = Math.PI / 12
        for (let i = 0; i <= cycles * 4; i++) {
          const progress = i / (cycles * 4)
          const frame = Math.round(start + progress * duration)
          const angle = progress * Math.PI * 2 * cycles
          const val = basePhi + Math.sin(angle) * amp
          timeline.addKeyframe('camera3D.phi', frame, val, EASE_IN_OUT)
        }
      },
    },
    {
      label: '3D Zoom In',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera3D
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera3D.radius', start, cam.radius, EASE_IN_OUT)
        timeline.addKeyframe(
          'camera3D.radius',
          end,
          cam.radius * 0.4,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: '3D Zoom Out',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera3D
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera3D.radius', start, cam.radius, EASE_IN_OUT)
        timeline.addKeyframe(
          'camera3D.radius',
          end,
          cam.radius * 2.5,
          EASE_IN_OUT,
        )
      },
    },
  ]
}

function cameraPresets(): RawPresetDef[] {
  return [
    {
      label: 'Zoom In',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.zoom', start, cam.zoom, EASE_IN_OUT)
        timeline.addKeyframe('camera.zoom', end, cam.zoom * 2.5, EASE_IN_OUT)
      },
    },
    {
      label: 'Zoom Out',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.zoom', start, cam.zoom, EASE_IN_OUT)
        timeline.addKeyframe('camera.zoom', end, cam.zoom * 0.4, EASE_IN_OUT)
      },
    },
    {
      label: 'Pan Left',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.x', start, cam.position[0], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.x',
          end,
          cam.position[0] - 0.5,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Pan Right',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.x', start, cam.position[0], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.x',
          end,
          cam.position[0] + 0.5,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Pan Up',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.y', start, cam.position[1], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.y',
          end,
          cam.position[1] + 0.5,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Pan Down',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.y', start, cam.position[1], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.y',
          end,
          cam.position[1] - 0.5,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Swipe LR',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, mid, end } = frameRange(timeline)
        timeline.addKeyframe(
          'camera.x',
          start,
          cam.position[0] - 0.3,
          EASE_IN_OUT,
        )
        timeline.addKeyframe('camera.y', start, cam.position[1], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.x',
          mid,
          cam.position[0] + 0.3,
          EASE_IN_OUT,
        )
        timeline.addKeyframe(
          'camera.x',
          end,
          cam.position[0] - 0.3,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Swipe UD',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, mid, end } = frameRange(timeline)
        timeline.addKeyframe(
          'camera.y',
          start,
          cam.position[1] - 0.3,
          EASE_IN_OUT,
        )
        timeline.addKeyframe('camera.x', start, cam.position[0], EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.y',
          mid,
          cam.position[1] + 0.3,
          EASE_IN_OUT,
        )
        timeline.addKeyframe(
          'camera.y',
          end,
          cam.position[1] - 0.3,
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Reveal',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.zoom', start, cam.zoom * 5, EASE_OUT)
        timeline.addKeyframe('camera.x', start, cam.position[0] + 0.2, EASE_OUT)
        timeline.addKeyframe('camera.y', start, cam.position[1] - 0.1, EASE_OUT)
        timeline.addKeyframe('camera.zoom', end, cam.zoom, EASE_OUT)
        timeline.addKeyframe('camera.x', end, cam.position[0], EASE_OUT)
        timeline.addKeyframe('camera.y', end, cam.position[1], EASE_OUT)
      },
    },
    {
      label: 'Implode',
      apply(flame, timeline) {
        const cam = flame.renderSettings.camera
        if (!cam) return
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('camera.zoom', start, cam.zoom, EASE_IN_OUT)
        timeline.addKeyframe('camera.x', start, cam.position[0], EASE_IN_OUT)
        timeline.addKeyframe('camera.y', start, cam.position[1], EASE_IN_OUT)
        timeline.addKeyframe('camera.zoom', end, cam.zoom * 0.15, EASE_IN_OUT)
        timeline.addKeyframe(
          'camera.x',
          end,
          cam.position[0] + 0.1,
          EASE_IN_OUT,
        )
        timeline.addKeyframe(
          'camera.y',
          end,
          cam.position[1] - 0.1,
          EASE_IN_OUT,
        )
      },
    },
  ]
}

function renderPresets(): RawPresetDef[] {
  return [
    {
      label: 'Skip 1→15',
      apply(_flame, timeline) {
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('skipIters', start, 1, LINEAR)
        timeline.addKeyframe('skipIters', end, 15, LINEAR)
      },
    },
    {
      label: 'Skip 15→1',
      apply(_flame, timeline) {
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('skipIters', start, 15, LINEAR)
        timeline.addKeyframe('skipIters', end, 1, LINEAR)
      },
    },
    {
      label: 'Contrast ↑',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.contrast
        timeline.addKeyframe('contrast', start, v, EASE_IN_OUT)
        timeline.addKeyframe('contrast', end, Math.min(20, v * 3), EASE_IN_OUT)
      },
    },
    {
      label: 'Contrast ↓',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.contrast
        timeline.addKeyframe('contrast', start, v, EASE_IN_OUT)
        timeline.addKeyframe(
          'contrast',
          end,
          Math.max(0.01, v * 0.3),
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Gamma ↑',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.gamma
        timeline.addKeyframe('gamma', start, v, EASE_IN_OUT)
        timeline.addKeyframe('gamma', end, Math.min(8, v * 2), EASE_IN_OUT)
      },
    },
    {
      label: 'Gamma ↓',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.gamma
        timeline.addKeyframe('gamma', start, v, EASE_IN_OUT)
        timeline.addKeyframe('gamma', end, Math.max(0.1, v * 0.5), EASE_IN_OUT)
      },
    },
    {
      label: 'Expo Flash',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        const v = flame.renderSettings.exposure
        timeline.addKeyframe('exposure', start, v, EASE_OUT)
        timeline.addKeyframe('exposure', mid, v + 3, EASE_OUT)
        timeline.addKeyframe('exposure', end, v, EASE_OUT)
      },
    },
    {
      label: 'Vibrancy ↑',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.vibrancy
        timeline.addKeyframe('vibrancy', start, v, EASE_IN_OUT)
        timeline.addKeyframe('vibrancy', end, Math.min(3, v * 2), EASE_IN_OUT)
      },
    },
    {
      label: 'Vibrancy ↓',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.vibrancy
        timeline.addKeyframe('vibrancy', start, v, EASE_IN_OUT)
        timeline.addKeyframe('vibrancy', end, Math.max(0, v * 0.3), EASE_IN_OUT)
      },
    },
    {
      label: 'Glow Pulse',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        const v = flame.renderSettings.highlightPower ?? 0.5
        timeline.addKeyframe('highlightPower', start, v, EASE_IN_OUT)
        timeline.addKeyframe('highlightPower', mid, v + 0.6, EASE_IN_OUT)
        timeline.addKeyframe('highlightPower', end, v, EASE_IN_OUT)
      },
    },
  ]
}

function colorPresets(): RawPresetDef[] {
  return [
    {
      label: 'Colors Shift',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          timeline.addKeyframe(
            `transform.${tidStr}.color.x`,
            start,
            t.color.x,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.y`,
            start,
            t.color.y,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.x`,
            mid,
            random01(),
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.y`,
            mid,
            random01(),
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.x`,
            end,
            random01(),
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.y`,
            end,
            random01(),
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      label: 'Colors Swap',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          timeline.addKeyframe(
            `transform.${tidStr}.color.x`,
            start,
            t.color.x,
            LINEAR,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.y`,
            start,
            t.color.y,
            LINEAR,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.x`,
            end,
            1 - t.color.y,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.color.y`,
            end,
            1 - t.color.x,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      // Palette phase sweep (renamed from the ambiguous "Phase 0→1"). Needs an
      // active palette to be visible — sweeps the colour mapping a full cycle.
      label: 'Palette Sweep',
      apply(_flame, timeline) {
        const { start, end } = frameRange(timeline)
        timeline.addKeyframe('palettePhase', start, 0, LINEAR)
        timeline.addKeyframe('palettePhase', end, 1, LINEAR)
      },
    },
    {
      label: 'Palette Bounce',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        const v = flame.renderSettings.palettePhase
        timeline.addKeyframe('palettePhase', start, v, EASE_IN_OUT)
        timeline.addKeyframe('palettePhase', mid, (v + 0.5) % 1, EASE_IN_OUT)
        timeline.addKeyframe('palettePhase', end, v, EASE_IN_OUT)
      },
    },
    {
      label: 'Palette Speed Up',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const v = flame.renderSettings.paletteSpeed
        timeline.addKeyframe('paletteSpeed', start, v, EASE_IN_OUT)
        timeline.addKeyframe(
          'paletteSpeed',
          end,
          Math.min(30, v * 2),
          EASE_IN_OUT,
        )
      },
    },
    {
      label: 'Palette Speed Wave',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        const v = flame.renderSettings.paletteSpeed
        const peak = Math.min(30, Math.max(v, 3))
        timeline.addKeyframe('paletteSpeed', start, v, EASE_IN_OUT)
        timeline.addKeyframe('paletteSpeed', mid, peak, EASE_IN_OUT)
        timeline.addKeyframe('paletteSpeed', end, v, EASE_IN_OUT)
      },
    },
  ]
}

function affinePresets(): RawPresetDef[] {
  return [
    {
      // Uniform scale lives on the matrix diagonal a (x) and e (y).
      label: 'Scale Up',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          const aff = t.postAffine
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            start,
            aff.a,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            start,
            aff.e,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            end,
            aff.a * 1.5,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            end,
            aff.e * 1.5,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      label: 'Scale Down',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          const aff = t.postAffine
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            start,
            aff.a,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            start,
            aff.e,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            end,
            aff.a * 0.5,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            end,
            aff.e * 0.5,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      label: 'Rotate 90°',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          // The affine matrix is [[a, b], [d, e]] (c, f are translation).
          // Post-multiply by a 90° rotation R = [[0, -1], [1, 0]]:
          //   [[a b] [d e]] · R = [[b -a] [e -d]]
          const { a, b, d, e } = t.postAffine
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            start,
            a,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.b`,
            start,
            b,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.d`,
            start,
            d,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            start,
            e,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.a`,
            end,
            b,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.b`,
            end,
            -a,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.d`,
            end,
            e,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.e`,
            end,
            -d,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      // Spin the final (post-IFS) transform a full turn — with Symmetry applied
      // this reads as a rotating kaleidoscope. Matrix [[a b] [d e]] = rotation.
      label: 'Kaleidoscope',
      apply(_flame, timeline) {
        const { start, end } = frameRange(timeline)
        const steps = 8
        for (let i = 0; i <= steps; i++) {
          const frame = Math.round(start + ((end - start) * i) / steps)
          const theta = (Math.PI * 2 * i) / steps
          const cos = Math.cos(theta)
          const sin = Math.sin(theta)
          timeline.addKeyframe('finalTransform.a', frame, cos, LINEAR)
          timeline.addKeyframe('finalTransform.b', frame, -sin, LINEAR)
          timeline.addKeyframe('finalTransform.d', frame, sin, LINEAR)
          timeline.addKeyframe('finalTransform.e', frame, cos, LINEAR)
        }
      },
    },
    {
      // Breathe the whole image via a uniform final-transform scale pulse.
      label: 'Bloom',
      apply(_flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        timeline.addKeyframe('finalTransform.a', start, 1, EASE_IN_OUT)
        timeline.addKeyframe('finalTransform.e', start, 1, EASE_IN_OUT)
        timeline.addKeyframe('finalTransform.a', mid, 1.4, EASE_IN_OUT)
        timeline.addKeyframe('finalTransform.e', mid, 1.4, EASE_IN_OUT)
        timeline.addKeyframe('finalTransform.a', end, 1, EASE_IN_OUT)
        timeline.addKeyframe('finalTransform.e', end, 1, EASE_IN_OUT)
      },
    },
    {
      // Sway the x-from-y shear (coefficient b) back and forth.
      label: 'Shear Sway',
      apply(flame, timeline) {
        const { start, mid, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          const b = t.postAffine.b
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.b`,
            start,
            b,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.b`,
            mid,
            b + 0.4,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.b`,
            end,
            b,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      label: 'Prob Shift',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        const entries = recordEntries(flame.transforms)
        const total = entries.reduce((s, [, t]) => s + t.probability, 0)
        // Generate random shares, then normalize so they sum to total
        const rawValues = entries.map(() => random01())
        const rawSum = rawValues.reduce((s, v) => s + v, 0)
        for (let i = 0; i < entries.length; i++) {
          const [tid, t] = entries[i]!
          const tidStr = String(tid)
          timeline.addKeyframe(
            `transform.${tidStr}.probability`,
            start,
            t.probability,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.probability`,
            end,
            (rawValues[i]! / rawSum) * total,
            EASE_IN_OUT,
          )
        }
      },
    },
    {
      label: 'Wiggle',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          for (const affine of ['preAffine', 'postAffine'] as const) {
            // a, b, d, e are the 2×2 matrix (c, f are translation).
            for (const coeff of ['a', 'b', 'd', 'e'] as const) {
              const v = t[affine][coeff]
              timeline.addKeyframe(
                `transform.${tidStr}.${affine}.${coeff}`,
                start,
                v,
                EASE_IN_OUT,
              )
              timeline.addKeyframe(
                `transform.${tidStr}.${affine}.${coeff}`,
                end,
                v + (random01() - 0.5) * 0.3,
                EASE_IN_OUT,
              )
            }
          }
        }
      },
    },
    {
      // Drift = translate the transform. Translation is c (x) and f (y).
      label: 'Drift',
      apply(flame, timeline) {
        const { start, end } = frameRange(timeline)
        for (const [tid, t] of recordEntries(flame.transforms)) {
          const tidStr = String(tid)
          const aff = t.postAffine
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.c`,
            start,
            aff.c,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.f`,
            start,
            aff.f,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.c`,
            end,
            aff.c + (random01() - 0.5) * 0.6,
            EASE_IN_OUT,
          )
          timeline.addKeyframe(
            `transform.${tidStr}.postAffine.f`,
            end,
            aff.f + (random01() - 0.5) * 0.6,
            EASE_IN_OUT,
          )
        }
      },
    },
  ]
}
